import Skeleton from "@/components/Skeleton/Skeleton";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Form, InputNumber, Modal, Select, message } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { ReactNode, useState, useMemo } from "react";
import { useGetProductsSearch, usePatchProducts } from "../../useProducts";
import DeleteProxyAction from "../DeleteProxyAction/DeleteProxyAction";
import styles from "./ManageProxyAction.module.scss";

export default function ManageProxyAction({
  state,
  children,
  multiple,
  proxyIds,
  sourceId,
  multiplier,
  proxyIdsDisabled,
  sourceIdDisabled,
  deletable,
  disabled,
}: {
  state: "add" | "edit";
  children: ReactNode;
  multiple?: boolean;
  proxyIds?: number[];
  sourceId?: number;
  multiplier?: number;
  proxyIdsDisabled?: boolean;
  sourceIdDisabled?: boolean;
  deletable?: boolean;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [form] = useForm();
  const bulkPatch = usePatchProducts();
  const translation = useTranslation();
  const analytics = useAnalytics();

  const currentProxyIds = useWatch("proxyIds", form) || [];
  const currentSourceId = useWatch("sourceId", form) || [];

  const selectedIds = useMemo(() => {
    return [...(proxyIds || []), sourceId].filter((id): id is number => id !== undefined);
  }, [proxyIds, sourceId]);

  const showModal = () => {
    form.resetFields();
    form.setFieldsValue({
      proxyIds,
      sourceId,
      multiplier: multiplier || 1,
    });
    setSearch(undefined);
    setIsOpen(true);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        bulkPatch
          .mutateAsync({
            productIds: values.proxyIds,
            patch: { proxy_id: values.sourceId, proxy_weight: values.multiplier },
          })
          .then(() => {
            if (values.proxyIds.length === 1) {
              analytics.track(`Proxy ${state === "edit" ? "Updated" : "Created"}`);
              message.success(`Proxy ${state === "edit" ? "updated" : "created"}.`);
            } else {
              analytics.track(`Proxies ${state === "edit" ? "Updated" : "Created"}`);
              message.success(`Proxies ${state === "edit" ? "updated" : "created"}.`);
            }
            setIsOpen(false);
          })
          .catch(() => {
            message.error(translation.errors.general);
          });
      })
      .catch(() => {});
  };

  const { data, isLoading, fetchNextPage, hasNextPage } = useGetProductsSearch({
    search,
    selectedIds,
  });

  const allProducts = useMemo(() => {
    return data?.pages.flatMap((page) => page.data.data) || [];
  }, [data]);

  const proxyProductOptions = useMemo(() => {
    return allProducts.map((product) => ({
      value: product.id,
      label: product.label,
      disabled: product.id === currentSourceId,
    }));
  }, [allProducts, currentSourceId]);

  const sourceProductOptions = useMemo(() => {
    return allProducts.map((product) => ({
      value: product.id,
      label: product.label,
      disabled: (currentProxyIds || []).includes(product.id),
    }));
  }, [allProducts, currentProxyIds]);

  const onSearch = (value: string) => {
    if (value.trim() === "") {
      setSearch(undefined);
    } else {
      setSearch(value);
    }
  };

  const onPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { target } = e;
    if (target instanceof HTMLElement) {
      if (target.scrollTop + target.offsetHeight === target.scrollHeight && hasNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <>
      <span onClick={() => disabled !== true && showModal()}>{children}</span>
      <Modal
        open={isOpen}
        width={800}
        wrapClassName={styles.ProxyModal}
        onCancel={handleCancel}
        closable={false}
        footer={
          <div className={styles.footer}>
            <div>
              {deletable === true && (
                <DeleteProxyAction
                  disabled={isLoading}
                  proxyIds={currentProxyIds}
                  onSuccess={() => setIsOpen(false)}
                >
                  <Button
                    disabled={isLoading}
                    danger
                  >
                    Delete proxy
                  </Button>
                </DeleteProxyAction>
              )}
            </div>
            <div>
              <Button
                onClick={handleCancel}
                disabled={bulkPatch.isLoading}
              >
                Cancel
              </Button>{" "}
              <Button
                type="primary"
                disabled={bulkPatch.isLoading || isLoading}
                loading={bulkPatch.isLoading}
                onClick={onSubmit}
              >
                {state === "edit" ? "Update" : "Add"} proxy
              </Button>
            </div>
          </div>
        }
        okText={"Relate products"}
        okButtonProps={{
          disabled: bulkPatch.isLoading,
          loading: bulkPatch.isLoading,
        }}
        cancelButtonProps={{
          disabled: bulkPatch.isLoading,
        }}
      >
        <div className={styles.header}>
          Manage proxies
          <CloseOutlined
            className={styles.close}
            onClick={handleCancel}
          />
        </div>
        <div className={styles.container}>
          <div className={styles.intro}>
            When you do not have primary data for all products you can use the primary uploaded BOM
            of a product as a source to create proxies from other similar products where you might
            not have all data available. This can help your estimation when primary data is not
            available for all products.
          </div>
          <div className={styles.form}>
            <Form
              form={form}
              onKeyUp={(e) => {
                if (e.code === "Enter") onSubmit();
              }}
            >
              <div className={styles["form-item"]}>
                <span className={styles["form-item-header"]}>
                  Proxy product{multiple ? "s" : ""}
                </span>
                <p>
                  These products have no BOM and will inherit the data from the primary source
                  product.
                </p>
                <Form.Item
                  name="proxyIds"
                  rules={[
                    {
                      required: true,
                      message: "At least 1 proxy product is required.",
                    },
                    () => ({
                      validator(_, value) {
                        return !(value || []).includes(sourceId)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Source product can not be included in proxy products.")
                            );
                      },
                    }),
                  ]}
                >
                  {isLoading ? (
                    <Skeleton style={{ width: "100%", height: "30px" }} />
                  ) : (
                    <Select
                      placeholder={`Select proxy product${multiple ? "s" : ""}...`}
                      mode="multiple"
                      disabled={bulkPatch.isLoading || proxyIdsDisabled === true}
                      maxCount={multiple ? undefined : 1}
                      showSearch
                      onSearch={onSearch}
                      allowClear
                      optionFilterProp="label"
                      onPopupScroll={onPopupScroll}
                      options={proxyProductOptions}
                    />
                  )}
                </Form.Item>
              </div>
              <div className={styles["form-item"]}>
                <span className={styles["form-item-header"]}>Primary source product</span>
                <p>This can only be a product where you have uploaded primary BOM data for.</p>
                <Form.Item
                  dependencies={["proxyIds"]}
                  name="sourceId"
                  rules={[
                    {
                      required: true,
                      message: "At least 1 source product is required.",
                    },
                    () => ({
                      validator(_, value) {
                        return !(currentProxyIds || []).includes(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Source product can not be included in proxy products.")
                            );
                      },
                    }),
                  ]}
                >
                  {isLoading ? (
                    <Skeleton style={{ width: "100%", height: "30px" }} />
                  ) : (
                    <Select
                      placeholder={"Select source product..."}
                      allowClear
                      showSearch
                      onSearch={onSearch}
                      disabled={bulkPatch.isLoading || sourceIdDisabled}
                      loading={isLoading}
                      optionFilterProp="label"
                      onPopupScroll={onPopupScroll}
                      options={sourceProductOptions}
                    />
                  )}
                </Form.Item>
              </div>
              <div className={styles["form-item"]}>
                <span className={styles["form-item-header"]}>Material mass multiplier</span>
                <p>
                  The multiplier will recalculate the mass of the materials so you can easily create
                  a more realistic impact calculation.
                </p>
                <div className={styles.input}>
                  <Form.Item
                    noStyle
                    name="multiplier"
                    rules={[
                      {
                        required: true,
                        message: "Required.",
                      },
                    ]}
                  >
                    <InputNumber
                      onFocus={(e) => e.target.select()}
                      disabled={bulkPatch.isLoading}
                      step={0.1}
                      min={0}
                    />
                  </Form.Item>
                  <span>You can edit this later in the selected products.</span>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </Modal>
    </>
  );
}
